import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { StartDrill } from '@/components/training/StartDrill';
import { loadTrainingDrill, type TrainingDrill } from '@/lib/training-program/drill-loader';
import { getLatestTrainingPlan } from '@/lib/api/training-program';
import type { TrainingPlan } from '@/types/curriculum';

export default function StartDrillPage() {
  const { drillId } = useParams<{ drillId: string }>();
  const navigate = useNavigate();
  const [drill, setDrill] = useState<TrainingDrill | null>(null);
  const [loading, setLoading] = useState(true);
  const [isValidPlanDrill, setIsValidPlanDrill] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!drillId) {
      setDrill(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    const loadData = async () => {
      // Load both the drill and the active plan
      const [drillData, planResponse] = await Promise.all([
        loadTrainingDrill(drillId),
        getLatestTrainingPlan().catch(() => null),
      ]);

      if (cancelled) return;

      // Check if drill is in the user's active plan
      const planJson = planResponse?.plan?.plan_json as TrainingPlan | undefined;
      let drillInPlan = false;
      if (planJson?.weeks) {
        drillInPlan = planJson.weeks.some((week) =>
          week.sessions.some((session) => session.drill_id === drillId)
        );
      }

      setDrill(drillData);
      setIsValidPlanDrill(drillInPlan);
      setLoading(false);
    };

    loadData().catch(() => {
      if (!cancelled) {
        setDrill(null);
        setIsValidPlanDrill(false);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [drillId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!drill || !drillId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <p className="text-sm text-muted-foreground">Drill not found.</p>
      </div>
    );
  }

  // Log warning for drills accessed outside the active plan (telemetry opportunity)
  if (!isValidPlanDrill) {
    console.warn('[StartDrillPage] Drill accessed outside active plan:', drillId);
  }

  {/* UX-AUDIT-V3-101: Pass training-program context for return-to-origin */}
  return (
    <StartDrill
      drill={drill}
      onBack={() => navigate(-1)}
      onStartExecution={(id) => navigate(`/training-program/execute/${id}`, { state: { from: 'training-program' } })}
    />
  );
}
